import { appBarMeta } from "@constants/appbar";
import { events } from "@constants/tracking";
import { PlaySavedPodcastsButton } from "@features/podcasts/components/PlaySavedPodcastsButton";
import { PodcastListItem } from "@features/podcasts/components/PodcastListItem";
import { usePodcasts } from "@features/podcasts/hooks/usePodcasts";
import { useSavedPodcasts } from "@features/podcasts/hooks/useSavedPodcasts";
import type { Document } from "@features/podcasts/types";
import { isWeb } from "@helpers/platform";
import { useAppTheme } from "@hooks/useAppTheme";
import { useDevice } from "@hooks/useDevice";
import Box from "@memorang/ui/components/Box";
import Container from "@memorang/ui/components/Container";
import { CustomAppBar } from "@memorang/ui/components/CustomAppbar";
import CustomTabs from "@memorang/ui/components/CustomTabs";
import EmptyState from "@memorang/ui/components/EmptyView";
import Loader from "@memorang/ui/components/Loader";
import { trackCustomEvent } from "analytics";
import CustomListSubheaderContainer from "components/CustomListSubheaderContainer";
import DocumentTitle from "components/DocumentTitle";
import { useNavigation } from "expo-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SectionList } from "react-native";
import { Card, Divider, Searchbar } from "react-native-paper";
import EmptyStateBookmark from "../../../../assets/empty_state_bookmark.svg";

type TabProps = {
	searchQuery: string;
	onSearchChange: (query: string) => void;
};

type PodcastSection = {
	type: string;
	podcasts: Document[];
};

const useFilteredPodcasts = (
	sections: PodcastSection[],
	searchQuery: string,
) => {
	return useMemo(() => {
		if (!searchQuery.trim()) {
			return sections.map(({ type, podcasts }) => ({
				title: type,
				data: podcasts,
			}));
		}

		const query = searchQuery.toLowerCase();
		return sections
			.map(({ type, podcasts }) => ({
				title: type,
				data: podcasts.filter(
					(podcast) =>
						podcast.content.title.toLowerCase().includes(query) ||
						podcast.metadata.author.toLowerCase().includes(query) ||
						podcast.tags?.some((tag) =>
							tag.label?.toLowerCase().includes(query),
						),
				),
			}))
			.filter(({ data }) => data.length > 0);
	}, [sections, searchQuery]);
};

type PodcastsListProps = {
	sections: { title: string; data: Document[] }[];
	searchQuery: string;
	onSearchChange: (query: string) => void;
	placeholder: string;
	totalCount: number;
	showPlayButton?: boolean;
	emptySearchTitle?: string;
	emptySearchSubtitle?: string;
};

const PodcastsList = ({
	sections,
	searchQuery,
	onSearchChange,
	placeholder,
	totalCount,
	showPlayButton = false,
	emptySearchTitle = "No podcasts found",
	emptySearchSubtitle = "Try adjusting your search terms or browse all podcasts",
}: PodcastsListProps) => {
	const theme = useAppTheme();

	const renderItem = ({ item }: { item: Document }) => {
		return <PodcastListItem podcast={item} />;
	};

	const renderSectionHeader = ({
		section: { title },
	}: {
		section: { title: string };
	}) => (
		<CustomListSubheaderContainer
			style={{
				paddingTop: 16,
				paddingBottom: 16,
				backgroundColor: showPlayButton
					? undefined
					: theme.colors.elevation.level1,
			}}
		>
			{title}
		</CustomListSubheaderContainer>
	);

	return (
		<Box flex={1}>
			<Card.Title
				title={`${totalCount} ${showPlayButton ? "Saved " : ""}Podcasts`}
				titleVariant="titleSmall"
				titleStyle={{
					paddingVertical: 12,
				}}
			/>
			<Divider />
			<Box padding={16} paddingBottom={8}>
				<Searchbar
					placeholder={placeholder}
					onChangeText={onSearchChange}
					value={searchQuery}
				/>
			</Box>
			{showPlayButton && (
				<Box padding={16} paddingTop={8}>
					<PlaySavedPodcastsButton visible={!searchQuery.trim()} />
				</Box>
			)}
			{searchQuery.trim() && sections.length === 0 ? (
				<EmptyState
					title={emptySearchTitle}
					subtitle={emptySearchSubtitle}
					cta="Clear Search"
					handleAction={() => onSearchChange("")}
				/>
			) : (
				<SectionList
					sections={sections}
					keyExtractor={(item) => item.id.toString()}
					renderItem={renderItem}
					renderSectionHeader={renderSectionHeader}
					showsVerticalScrollIndicator={isWeb}
					stickySectionHeadersEnabled={false}
					style={{ flexGrow: 1 }}
				/>
			)}
		</Box>
	);
};

const AllPodcastsTab = ({ searchQuery, onSearchChange }: TabProps) => {
	const { podcastsByType, isLoading, podcasts } = usePodcasts();
	const filteredSections = useFilteredPodcasts(podcastsByType, searchQuery);

	const filteredCount = useMemo(
		() =>
			filteredSections.reduce((acc, section) => acc + section.data.length, 0),
		[filteredSections],
	);

	if (isLoading) {
		return <Loader />;
	}

	return (
		<PodcastsList
			sections={filteredSections}
			searchQuery={searchQuery}
			onSearchChange={onSearchChange}
			placeholder="Search podcasts..."
			totalCount={searchQuery.trim() ? filteredCount : podcasts.length}
		/>
	);
};

const SavedPodcastsTab = ({ searchQuery, onSearchChange }: TabProps) => {
	const { savedPodcasts, savedPodcastsByType, isLoading } = useSavedPodcasts();
	const filteredSections = useFilteredPodcasts(
		savedPodcastsByType,
		searchQuery,
	);

	const filteredCount = useMemo(
		() =>
			filteredSections.reduce((acc, section) => acc + section.data.length, 0),
		[filteredSections],
	);

	if (isLoading) {
		return <Loader />;
	}

	if (savedPodcasts.length === 0) {
		return (
			<EmptyState
				title="No saved podcasts"
				subtitle="Save your favorite podcasts to find them here"
				image={EmptyStateBookmark}
			/>
		);
	}

	return (
		<PodcastsList
			sections={filteredSections}
			searchQuery={searchQuery}
			onSearchChange={onSearchChange}
			placeholder="Search saved podcasts..."
			totalCount={searchQuery.trim() ? filteredCount : savedPodcasts.length}
			showPlayButton
			emptySearchTitle="No saved podcasts found"
			emptySearchSubtitle="Try adjusting your search or clear to see all saved podcasts"
		/>
	);
};

const Podcasts = () => {
	const { t } = useTranslation();
	const { isMobile } = useDevice();
	const theme = useAppTheme();
	const [searchQuery, setSearchQuery] = useState("");
	const navigation = useNavigation<{ toggleDrawer?: () => void }>();

	return (
		<>
			<CustomAppBar
				title={t(appBarMeta.podcasts.title)}
				navigation={navigation}
				handleTrackEvent={() => {
					trackCustomEvent({
						eventName: events.commandBarClicked,
						option: "command_bar",
					});
				}}
			/>
			<DocumentTitle title={t(appBarMeta.podcasts.title)} />
			<Container
				style={{
					paddingVertical: isMobile ? 0 : 32,
					paddingHorizontal: isMobile ? 0 : 32,
				}}
				containerStyle={{ flex: 1 }}
			>
				<Box
					flex={1}
					backgroundColor={theme.colors.elevation.level1}
					borderRadius={theme.roundness * 3}
					overflow="hidden"
				>
					<CustomTabs
						mode="fixed"
						tabs={[
							{
								label: "All Podcasts",
								content: (
									<AllPodcastsTab
										searchQuery={searchQuery}
										onSearchChange={setSearchQuery}
									/>
								),
							},
							{
								label: "Saved",
								content: (
									<SavedPodcastsTab
										searchQuery={searchQuery}
										onSearchChange={setSearchQuery}
									/>
								),
							},
						]}
					/>
				</Box>
			</Container>
		</>
	);
};

export default Podcasts;
